/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {DataProviderAssignmentDetailsComponent} from './components/battery/attribute-assignment/data-provider-assignment-details/data-provider-assignment-details.component';
import {BatteryPassEcosystemComponent} from './components/battery/battery-pass-ecosystem/battery-pass-ecosystem.component';
import {CreateBatteryPassComponent} from './components/battery/create-battery-pass/create-battery-pass.component';
import {EditBatteryPassComponent} from './components/battery/edit-battery-pass/edit-battery-pass.component';
import {ListBatteryPassesComponent} from './components/battery/list-battery-passes/list-battery-passes.component';
import {ListRemovedBatteryPassesComponent} from './components/battery/list-removed-battery-passes/list-removed-battery-passes.component';
import {PrivateBatteryPassViewerComponent} from './components/battery/private-battery-pass-viewer/private-battery-pass-viewer.component';
import {PublicBatteryPassViewerComponent} from './components/battery/public-battery-pass-viewer/public-battery-pass-viewer.component';
import {DppDetailViewComponent} from './components/dpp/dpp-detail-view/dpp-detail-view.component';
import {ErrorComponent} from './components/error/error.component';
import {GenericSubmodelEditorComponent} from './components/dpp/generic-submodel-editor/generic-submodel-editor.component';
import {HandoverDocumentationComponent} from './components/dpp/handover-documentation/handover-documentation.component';
import {ListPassportComponent} from './components/dpp/list-passport/list-passport.component';
import {PublicDppDetailViewComponent} from './components/dpp/public/public-dpp-detail-view/public-dpp-detail-view.component';
import {PublicSubmodelViewerDispatcherComponent} from './components/dpp/public/public-submodel-viewer-dispatcher/public-submodel-viewer-dispatcher.component';
import {SubmodelViewerDispatcherComponent} from './components/dpp/submodel-viewer-dispatcher/submodel-viewer-dispatcher.component';
import {PublicViewerShellComponent} from './components/dpp/public-viewer-shell/public-viewer-shell.component';
import {EditUserComponent} from './components/user-management/edit-user/edit-user.component';
import {InviteUserComponent} from './components/user-management/invite-user/invite-user.component';
import {ListUserComponent} from './components/user-management/list-user/list-user.component';
import {PmdFeatureFlagGuard} from './guards/pmd-feature-flag-guard';
import {LoginComponent} from './components/login/login.component';
import {MachineLoginComponent} from './components/machine-documentation/machine-login/machine-login.component';
import {
  BATTERY_PASSPORT_TITLE,
  ERROR_PAGE_TITLE,
  isMachineDocumentation,
  MACHINE_DOCUMENTATION_TITLE,
} from '../models/Constants';
import {OverviewComponent} from './components/overview/overview.component';
import {BppFeatureFlagGuard} from './guards/bpp-feature-flag-guard';
import {authenticationGuard} from './guards/authentication.guard';
import {DataProviderAssignmentListComponent} from './components/battery/attribute-assignment/data-provider-assignment-list/data-provider-assignment-list.component';
import {ListAccessRequestsComponent} from './components/user-management/list-access-requests/list-access-requests.component';
import {EditAccessRequestComponent} from './components/user-management/edit-access-request/edit-access-request.component';
import {VerifyEmailComponent} from './components/verify-email/verify-email.component';
import {
  PRODUCT_ID_ENTRY_PREFIXES,
  productIdPrefixMatcher,
} from '../utils/product-id-url';

const childRoutes: Routes = [
  {
    path: '',
    redirectTo: isMachineDocumentation() ? 'dpp/list-passports' : 'dashboard',
    pathMatch: 'full',
    title: isMachineDocumentation()
      ? MACHINE_DOCUMENTATION_TITLE
      : BATTERY_PASSPORT_TITLE,
  },
  {
    path: 'dashboard',
    component: BatteryPassEcosystemComponent,
    title: BATTERY_PASSPORT_TITLE,
    canActivate: [BppFeatureFlagGuard],
  },
  {
    path: 'list-passports',
    component: ListBatteryPassesComponent,
    title: BATTERY_PASSPORT_TITLE,
    canActivate: [BppFeatureFlagGuard],
  },
  {
    path: 'list-removed-passports',
    component: ListRemovedBatteryPassesComponent,
    title: BATTERY_PASSPORT_TITLE,
    canActivate: [BppFeatureFlagGuard],
  },
  {
    path: 'add-passport',
    component: CreateBatteryPassComponent,
    title: BATTERY_PASSPORT_TITLE,
    canActivate: [BppFeatureFlagGuard],
  },
  {
    path: 'view-passport/:id',
    component: PrivateBatteryPassViewerComponent,
    title: BATTERY_PASSPORT_TITLE,
    canActivate: [BppFeatureFlagGuard],
  },
  {
    path: 'add-passport/:id',
    component: CreateBatteryPassComponent,
    title: BATTERY_PASSPORT_TITLE,
    canActivate: [BppFeatureFlagGuard],
  },
  {
    path: 'edit-passport/:id',
    component: EditBatteryPassComponent,
    title: BATTERY_PASSPORT_TITLE,
    canActivate: [BppFeatureFlagGuard],
  },
  {
    path: 'assign-attributes',
    component: DataProviderAssignmentListComponent,
    title: BATTERY_PASSPORT_TITLE,
    canActivate: [BppFeatureFlagGuard],
  },
  {
    path: 'assign-attributes/:id',
    component: DataProviderAssignmentDetailsComponent,
    title: BATTERY_PASSPORT_TITLE,
    canActivate: [BppFeatureFlagGuard],
  },
  {
    path: 'list-user',
    component: ListUserComponent,
    title: BATTERY_PASSPORT_TITLE,
  },
  {
    path: 'invite-user',
    component: InviteUserComponent,
    title: BATTERY_PASSPORT_TITLE,
  },
  {
    path: 'edit-user/:id',
    component: EditUserComponent,
    title: BATTERY_PASSPORT_TITLE,
  },
  {
    path: 'list-access-requests',
    component: ListAccessRequestsComponent,
    title: BATTERY_PASSPORT_TITLE,
  },
  {
    path: 'edit-access-request/:id',
    component: EditAccessRequestComponent,
    title: BATTERY_PASSPORT_TITLE,
  },
  {
    path: 'dpp/list-passports',
    component: ListPassportComponent,
    canActivate: [PmdFeatureFlagGuard],
    title: MACHINE_DOCUMENTATION_TITLE,
  },
  {
    path: 'dpp/view-passport/:id',
    component: DppDetailViewComponent,
    title: MACHINE_DOCUMENTATION_TITLE,
    canActivate: [PmdFeatureFlagGuard],
  },
  {
    path: 'dpp/view-passport/:aasId/submodel/create',
    component: GenericSubmodelEditorComponent,
    title: MACHINE_DOCUMENTATION_TITLE,
    canActivate: [PmdFeatureFlagGuard],
  },
  {
    path: 'dpp/view-passport/:aasId/submodel/create-handover-documentation',
    component: HandoverDocumentationComponent,
    title: MACHINE_DOCUMENTATION_TITLE,
    canActivate: [PmdFeatureFlagGuard],
  },
  {
    path: 'dpp/view-passport/:aasId/submodel/:submodelId',
    component: SubmodelViewerDispatcherComponent,
    title: MACHINE_DOCUMENTATION_TITLE,
    canActivate: [PmdFeatureFlagGuard],
  },
  {
    path: 'dpp/view-passport/:aasId/submodel/:submodelId/edit',
    component: GenericSubmodelEditorComponent,
    title: MACHINE_DOCUMENTATION_TITLE,
    canActivate: [PmdFeatureFlagGuard],
  },
];

const parentRoutes: Routes = [
  {
    path: 'login',
    component: isMachineDocumentation()
      ? MachineLoginComponent
      : LoginComponent,
    title: isMachineDocumentation()
      ? MACHINE_DOCUMENTATION_TITLE
      : BATTERY_PASSPORT_TITLE,
  },

  // Temporarily disabled feature 'Legal Disclaimer' for the release v1.3.0
  // The feature has been commented out because the official text is not yet available.
  // It will be re-enabled once the final content is provided.

  //{
  // path: 'disclaimer',
  // component: LegalDisclaimerComponent,
  // canActivate: [authenticationGuard],
  //  title: isMachineDocumentation()
  //    ? MACHINE_DOCUMENTATION_TITLE
  //    : BATTERY_PASSPORT_TITLE,
  // },
  {
    path: 'app',
    component: OverviewComponent,
    children: childRoutes,
    canActivate: [authenticationGuard],
    title: isMachineDocumentation()
      ? MACHINE_DOCUMENTATION_TITLE
      : BATTERY_PASSPORT_TITLE,
  },
  {
    path: 'public/dpp',
    component: PublicViewerShellComponent,
    title: MACHINE_DOCUMENTATION_TITLE,
    children: [
      {
        path: ':id',
        component: PublicDppDetailViewComponent,
      },
      {
        path: ':aasId/submodel/:submodelId',
        component: PublicSubmodelViewerDispatcherComponent,
      },
    ],
  },
  {
    path: 'public/iec61406/:ra/:issuer/:objectId',
    component: PublicBatteryPassViewerComponent,
    title: BATTERY_PASSPORT_TITLE,
  },
  // Short Product ID entry point (BL-6190, ADR-074 amendment): the short IEC
  // 61406 Identification Link (`/id/{ObjectId}[/...]`) and GS1 Digital Link
  // URIs keyed on a supported primary key (`/01/{GTIN}[/21/{serial}]` etc.),
  // one matcher over the combined prefix list (architect decision,
  // 2026-09-23: a single route, not one per standard, since routing itself
  // never needs to distinguish them). Matched case-insensitively; the `**`
  // child accepts any depth so the segment layout after the prefix is left
  // to the data owner, as both IEC 61406-1 and GS1 Digital Link permit.
  {
    matcher: productIdPrefixMatcher(PRODUCT_ID_ENTRY_PREFIXES),
    title: BATTERY_PASSPORT_TITLE,
    children: [
      {
        path: '**',
        component: PublicBatteryPassViewerComponent,
      },
    ],
  },
  {
    path: 'public/:id',
    component: PublicBatteryPassViewerComponent,
    title: BATTERY_PASSPORT_TITLE,
  },
  {
    path: 'error',
    component: ErrorComponent,
    title: ERROR_PAGE_TITLE,
  },
  {
    path: 'verify-email',
    component: VerifyEmailComponent,
    title: BATTERY_PASSPORT_TITLE,
  },
];

// Exported so app-routing.module.spec.ts can drive the real Router against
// this exact config without instantiating the routed components.
export const routes: Routes = [
  ...parentRoutes,
  {
    path: '',
    redirectTo: '/app',
    title: isMachineDocumentation()
      ? MACHINE_DOCUMENTATION_TITLE
      : BATTERY_PASSPORT_TITLE,
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: '/error',
    pathMatch: 'full',
    title: ERROR_PAGE_TITLE,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  declarations: [],
  exports: [RouterModule],
})
export class AppRoutingModule {}
